import { AfterViewInit, afterRenderEffect, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, Signal, signal, untracked, viewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { VenueIconComponent } from '@components/shared/venue-icon/venue-icon.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { SelectorType, VenueConfigurationEntry } from '@models/types/components/VenueConfigurations.type';
import { VENUE_ICON_IDS } from '@models/types/components/VenueIconIds.type';
import { LayoutService } from '@services/layout.service';
import { VenueIconLibraryService } from '@services/venue-icon-library.service';
import { VenueLayoutService } from '@services/venue-layout.service';
import { TranslationService } from '@services/translation.service';
import anime from 'animejs';

type VenueLayoutOption = VenueConfigurationEntry & { showIcon: boolean };

@Component({
    selector: 'app-venue-layout-container',
    imports: [
        ButtonComponent,
        VenueIconComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './venue-layout-container.component.html',
    styleUrl: './venue-layout-container.component.css',
    host: {
        '[class.can-scroll-up]':   'canScrollUp()',
        '[class.can-scroll-down]': 'canScrollDown()',
        '(scroll)':                'onScroll()',
        '(window:resize)':         'onScroll()'
    }
})
export class VenueLayoutContainerComponent implements AfterViewInit {

  // SERVICES
  protected readonly venueLayout:  VenueLayoutService      = inject(VenueLayoutService);
  protected readonly translation:  TranslationService      = inject(TranslationService);
  private   readonly library:      VenueIconLibraryService = inject(VenueIconLibraryService);
  private   readonly isMobile:     Signal<boolean>         = inject(LayoutService).isMobile;
  private   readonly injector:     Injector                = inject(Injector);
  private   readonly destroyRef:   DestroyRef              = inject(DestroyRef);

  // Fade de scroll: :host es el contenedor con overflow, este ElementRef mide su propio scroll.
  private   readonly hostEl: ElementRef<HTMLElement> = inject(ElementRef);

  protected readonly canScrollUp:   WritableSignal<boolean> = signal(false);
  protected readonly canScrollDown: WritableSignal<boolean> = signal(false);

  // ANIMACION DE APERTURA/CIERRE: mismo patron que menu-container y benefits-container.
  private   readonly containerEl  = viewChild<ElementRef<HTMLDivElement>>('venueLayoutContainer');
  protected readonly hideElement:  WritableSignal<boolean> = signal(true);
  private   readonly duration:     number                  = 400;

  // Corto a proposito: overflow-y auto en :host hace que overflow-x recorte lo que sobresale.
  private   readonly slideOffset:  number                  = 24;

  // El paso se reparte hasta este tope: el schema no limita las configuraciones y un paso fijo
  // alargaria la animacion (y el boton disparador deshabilitado) sin control.
  private   readonly staggerStep:  number                  = 40;
  private   readonly staggerTotal: number                  = 200;

  private   timeline?: anime.AnimeTimelineInstance;

  constructor() {
    // El servicio sobrevive al componente: solo limpia si esta instancia dejo algo a medias.
    this.destroyRef.onDestroy((): void => {
      if (!this.timeline || this.timeline.completed) return;

      this.timeline.pause();
      this.venueLayout.isAnimating.set(false);
    });
  }

  /**
   * Un icono vacio, retirado o cuya libreria no carga se trata como "sin icono" y cae al label:
   * descartar la entrada esconderia una configuracion que funciona.
   */
  protected readonly options: Signal<VenueLayoutOption[]> = computed((): VenueLayoutOption[] => {

    const iconsUsable: boolean = !this.library.unavailable();

    return this.venueLayout.venueConfigs.configurations().map((entry: VenueConfigurationEntry): VenueLayoutOption => ({
      ...entry,
      showIcon: iconsUsable && entry.selectorType === SelectorType.Icon && VENUE_ICON_IDS.includes(entry.icon)
    }));

  });

  // Entradas de texto. Sin color: glass aparcado para el redesign, el defecto del boton es el que toca.
  protected readonly buttonType:   ButtonType          = ButtonType.Pill;

  // Default, no Pill: la rama `pill` del boton solo proyecta el icono, y aqui hacen falta icono Y
  // label a la vez. La clase visual `pill` viaja en `iconCustom` (el getter `classes` descarta
  // 'default'), asi que el SVG sigue a 24px por `.btn.pill app-venue-icon svg` de main.css.
  protected readonly iconButtonType: ButtonType        = ButtonType.Default;

  // El box del <button> vive en ButtonComponent: solo se puede tocar via utilidades de Tailwind.
  protected readonly iconCustom:   string[]            = [
    'pill flex items-center justify-end h-12 min-w-12 p-0 overflow-hidden'
  ];

  // Sin `size-12`: era la causa del desbordamiento de los labels largos.
  protected readonly textCustom:   string[]            = ['max-w-full truncate px-4'];

  // Se re-mide cuando cambia options() o la visibilidad: la lista puede crecer/encoger sin que el
  // host cambie de tamano, y cerrado no hay nada que medir, asi que el alto real solo existe al abrir.
  private readonly scrollFadeEffect = afterRenderEffect((): void => {
    this.options();
    this.hideElement();
    this.updateScrollState();
  });

  public ngAfterViewInit(): void {
    effect((): void => {

      const show: boolean = this.venueLayout.showContainer();

      // untracked: toggleContainer lee isMobile() y containerEl(); si no, el breakpoint reanima.
      untracked((): void => this.toggleContainer(show));

    }, { injector: this.injector });
  }

  // Scroll y resize: el overflow cambia tanto al desplazarse como al cambiar el alto del panel.
  protected onScroll(): void {
    this.updateScrollState();
  }

  private toggleContainer(show: boolean): void {

    const container: HTMLDivElement | undefined = this.containerEl()?.nativeElement;
    if (!container) return;

    // Sin esto, cada montaje animaria un cierre sobre un elemento ya oculto.
    if (!show && this.hideElement()) return;

    // En movil el panel vive en el bottom sheet, que ya anima su entrada y salida.
    if (this.isMobile()) {

      this.hideElement.set(!show);
      container.style.opacity = show ? '1' : '0';

      // La rama de escritorio deja opacidad inline al cerrar y aqui no la anima nadie.
      container.querySelectorAll('app-button')
        .forEach((button: Element): void => { (button as HTMLElement).style.removeProperty('opacity'); });

      return;
    }

    // Los cierres programaticos no pasan por el boton deshabilitado: puede haber una a medias.
    this.timeline?.pause();

    if (show) this.hideElement.set(false);

    const buttons:   HTMLElement[] = Array.from(container.querySelectorAll('app-button'));
    const staggered: boolean       = buttons.length > 1;
    // Sin redondear: `Math.round` subia el paso por encima del tope en divisiones no exactas
    // (13 botones -> 204ms) y con listas enormes lo dejaba en 0, quitando el stagger del todo.
    const step:      number        = staggered
      ? Math.min(this.staggerStep, this.staggerTotal / (buttons.length - 1))
      : 0;

    const containerStep = {
      targets:    container,
      translateX: show ? [this.slideOffset, 0] : [0, this.slideOffset],
      opacity:    show ? 1 : 0,
    };

    // [0, 1] fija el arranque: si no, el stagger no se nota y queda opacidad de un cierre previo.
    const buttonsStep = {
      targets: buttons,
      opacity: show ? [0, 1] : 0,
      delay:   staggered ? anime.stagger(step, { start: 0 }) : 0,
    };

    const overlap: string = staggered ? `-=${Math.round(this.duration / 2)}` : '';

    this.timeline = anime.timeline({
      begin:    (): void => this.venueLayout.isAnimating.set(true),
      complete: (): void => {
        if (!this.venueLayout.showContainer()) this.hideElement.set(true);
        this.venueLayout.isAnimating.set(false);
      },
      duration: this.duration,
      easing:   'easeInOutQuad',
    });

    // Orden invertido al cerrar: si no, el stagger se pierde bajo el fade del contenedor.
    if (show) {
      this.timeline.add(containerStep).add(buttonsStep, overlap);
    } else {
      this.timeline.add(buttonsStep).add(containerStep, overlap);
    }

  }

  private updateScrollState(): void {

    const el: HTMLElement = this.hostEl.nativeElement;
    const scrollable: boolean = el.scrollHeight - el.clientHeight > 1;

    this.canScrollUp.set(scrollable && el.scrollTop > 1);
    this.canScrollDown.set(scrollable && el.scrollTop + el.clientHeight < el.scrollHeight - 1);

  }

}
